import { supabase } from './api'

/**
 * Meetings — the Shop L10, run inside the app (0018).
 *
 * The presenter (an admin) starts a meeting, steps through the agenda and
 * ends it; everyone else follows along by polling `get_current_meeting`,
 * which carries the server's own clock so every phone derives the same
 * countdown. The recap is a time window over timestamps the other RPCs
 * already stamp, live while the meeting runs and frozen when it ends — see
 * the migration header for why that beats foreign keys.
 */

export interface MeetingRecap {
  headlines_shared: number
  headlines: { id: string; body: string }[]
  issues_solved: number
  issues_solved_list: { id: string; title: string }[]
  issues_reported: number
  issues_archived: number
  tasks_created: number
  tasks: { id: string; title: string }[]
  /**
   * 0025: cascading messages read out (no longer counted as headlines) and
   * messages sent to other teams. Optional because recaps frozen before 0025
   * do not have them.
   */
  cascades_shared?: number
  cascades_in?: { id: string; body: string; from: string }[]
  cascades_created?: number
  cascades_out?: { id: string; body: string; teams: string | null }[]
  rating_avg: number | null
  rating_count: number
}

export interface Meeting {
  id: string
  started_at: string
  started_by_name: string
  /** Index into AGENDA in meeting.ts, 0–5. */
  current_section: number
  section_started_at: string
  ended_at: string | null
  archived_solved: boolean
  /** Live while the meeting is open. */
  recap: MeetingRecap
  my_rating: number | null
  /** The database's now(), for measuring this device's clock skew. */
  server_now: string
}

export interface MeetingSummary {
  id: string
  started_at: string
  ended_at: string
  duration_minutes: number
  started_by_name: string
  archived_solved: boolean
  /** Live — people can still rate for an hour after the end. */
  rating_avg: number | null
  rating_count: number
  headlines_shared: number
  issues_solved: number
  tasks_created: number
  my_rating: number | null
  /** The frozen recap. */
  recap: MeetingRecap | null
}

function fail(error: { code?: string; message?: string }): Error {
  if (error.code === '28000') {
    return new Error('This link is not recognised. Ask Scott for a new one.')
  }
  if (error.code === '42501') {
    return new Error(error.message || 'You do not have permission to do that.')
  }
  if (error.code === '22023') {
    return new Error(error.message || 'That value is not allowed.')
  }
  return new Error(error.message || 'Something went wrong. Try again.')
}

/** The one open meeting, or null when none is running. */
export async function getCurrentMeeting(token: string): Promise<Meeting | null> {
  const { data, error } = await supabase.rpc('get_current_meeting', { p_token: token })
  if (error) throw fail(error)
  return (data as Meeting[] | null)?.[0] ?? null
}

export async function startMeeting(token: string): Promise<string> {
  const { data, error } = await supabase.rpc('start_meeting', { p_token: token })
  if (error) throw fail(error)
  return data as string
}

export async function setMeetingSection(
  token: string,
  meetingId: string,
  section: number,
): Promise<void> {
  const { error } = await supabase.rpc('set_meeting_section', {
    p_token: token,
    p_meeting_id: meetingId,
    p_section: section,
  })
  if (error) throw fail(error)
}

/** 1–10. Allowed while the meeting is open and for an hour after it ends. */
export async function rateMeeting(
  token: string,
  meetingId: string,
  rating: number,
): Promise<void> {
  const { error } = await supabase.rpc('rate_meeting', {
    p_token: token,
    p_meeting_id: meetingId,
    p_rating: rating,
  })
  if (error) throw fail(error)
}

/** Ends the meeting, optionally archives every Solved issue, returns the frozen recap. */
export async function endMeeting(
  token: string,
  meetingId: string,
  archiveSolved: boolean,
): Promise<MeetingRecap> {
  const { data, error } = await supabase.rpc('end_meeting', {
    p_token: token,
    p_meeting_id: meetingId,
    p_archive_solved: archiveSolved,
  })
  if (error) throw fail(error)
  return data as MeetingRecap
}

/** Ended meetings, newest first. */
export async function listMeetings(token: string, limit = 20): Promise<MeetingSummary[]> {
  const { data, error } = await supabase.rpc('list_meetings', {
    p_token: token,
    p_limit: limit,
  })
  if (error) throw fail(error)
  return (data as MeetingSummary[] | null) ?? []
}
